import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../utils/apiConfig';

/**
 * Custom hook for loading Hyperliquid wallet data
 * Fetches wallet stats, positions, and PnL history from REST API
 */
export function useWalletData(address) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [walletData, setWalletData] = useState(null);
  const [positions, setPositions] = useState([]);
  const [pnlHistory, setPnlHistory] = useState([]);
  const isInitialLoad = useRef(true);

  // Fetch wallet data
  const fetchWalletData = useCallback(async (showLoading = false) => {
    if (!address) {
      setLoading(false);
      return;
    }

    try {
      // Only show loading on initial load
      if (isInitialLoad.current || showLoading) {
        setLoading(true);
      }
      setError(null);

      console.log('📥 Fetching wallet data for:', address);

      // Fetch all data in parallel for faster loading
      const [walletResponse, positionsResponse, pnlResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/hyperliquid/wallet/${address}`),
        fetch(`${API_BASE_URL}/api/hyperliquid/wallet/${address}/positions`),
        fetch(`${API_BASE_URL}/api/hyperliquid/wallet/${address}/pnl`)
      ]);

      // Check responses
      if (!walletResponse.ok) {
        throw new Error(`Failed to fetch wallet data: ${walletResponse.status}`);
      }
      if (!positionsResponse.ok) {
        throw new Error(`Failed to fetch positions: ${positionsResponse.status}`);
      }
      if (!pnlResponse.ok) {
        throw new Error(`Failed to fetch PnL history: ${pnlResponse.status}`);
      }

      // Parse all responses in parallel
      const [walletResult, positionsResult, pnlResult] = await Promise.all([
        walletResponse.json(),
        positionsResponse.json(),
        pnlResponse.json()
      ]);

      console.log('✅ Wallet data loaded:', {
        stats: walletResult.success,
        positions: positionsResult.positions?.length || 0,
        pnlHistory: pnlResult.history?.length || 0
      });

      setWalletData(walletResult.stats);
      setPositions(positionsResult.positions || []);
      setPnlHistory(pnlResult.history || []);
      setLoading(false);
      isInitialLoad.current = false;
    } catch (err) {
      console.error('❌ Error fetching wallet data:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [address]);

  // Refresh wallet data (manual refresh shows loading)
  const refresh = useCallback(() => {
    fetchWalletData(true);
  }, [fetchWalletData]);

  // Load data on mount and when address changes
  useEffect(() => {
    isInitialLoad.current = true;
    fetchWalletData();
  }, [fetchWalletData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!address) return;

    const interval = setInterval(() => {
      fetchWalletData();
    }, 30000);

    return () => clearInterval(interval);
  }, [address, fetchWalletData]);

  return {
    loading,
    error,
    walletData,
    positions,
    pnlHistory,
    refresh
  };
}
